import React, { useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Empty, Select, Spin, Typography } from "antd";
import { apiRequest } from "util/services";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const { Text } = Typography;

const ANALYZABLE_TYPES = new Set([
  "open_text",
  "rating",
  "single_choice",
  "multiple_choice",
]);

const FeedbackCaptureWizardFields = ({ field, localState, setState }) => {
  const { t } = useTranslation();
  const [instruments, setInstruments] = useState([]);
  const [loadingInstruments, setLoadingInstruments] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [instrumentConfigs, setInstrumentConfigs] = useState({});

  const selectedInstrumentIds = localState.feedbackInstrumentIds || [];
  const questionSelections = localState.feedbackQuestionSelections || {};

  useEffect(() => {
    if (field.valueType !== "feedbackInstrument") return;
    let cancelled = false;
    setLoadingInstruments(true);
    setState((prev) => ({
      ...prev,
      feedbackInstrumentsLoaded: false,
      feedbackInstrumentsAvailable: false,
    }));
    apiRequest("feedback/instruments", "GET", null, true)
      .then((res) => {
        if (cancelled) return;
        const items = (res?.instruments || []).filter(
          (item) => item.type === "survey"
        );
        setInstruments(items);
        setState((prev) => ({
          ...prev,
          feedbackInstrumentsLoaded: true,
          feedbackInstrumentsAvailable: items.length > 0,
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingInstruments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [field.valueType]);

  useEffect(() => {
    if (field.valueType !== "feedbackQuestions") return;
    if (!selectedInstrumentIds.length) {
      setInstrumentConfigs({});
      return;
    }
    let cancelled = false;
    setLoadingConfig(true);
    Promise.all(
      selectedInstrumentIds.map((id) =>
        apiRequest(`feedback/instruments/${id}`, "GET", null, true).then(
          (res) => [id, res?.instrument?.config || {}]
        )
      )
    )
      .then((entries) => {
        if (cancelled) return;
        const next = {};
        entries.forEach(([id, config]) => {
          next[id] = config;
        });
        setInstrumentConfigs(next);
        setState((prev) => ({
          ...prev,
          feedbackInstrumentConfig: next,
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingConfig(false);
      });
    return () => {
      cancelled = true;
    };
  }, [field.valueType, selectedInstrumentIds, setState]);

  const instrumentTitleMap = useMemo(() => {
    const map = {};
    instruments.forEach((item) => {
      map[item.id] = item.title;
    });
    Object.entries(instrumentConfigs).forEach(([id, config]) => {
      if (!map[id] && config?.title) map[id] = config.title;
    });
    return map;
  }, [instruments, instrumentConfigs]);

  if (field.valueType === "feedbackInstrument") {
    const showEmptyState = !loadingInstruments && instruments.length === 0;

    return (
      <div>
        <h4>{field.title}</h4>
        {field.subtitle && <p>{field.subtitle}</p>}
        {showEmptyState ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div>{t("new_dashboard.feedbackCapture.no_instruments")}</div>
                <Text type="secondary">
                  {t("new_dashboard.feedbackCapture.no_instruments_hint")}
                </Text>
              </div>
            }
          >
            <Link to="/console/sonic-survey">
              <Button type="primary" icon={<PlusOutlined />}>
                {t("new_dashboard.feedbackCapture.create_instrument_cta")}
              </Button>
            </Link>
          </Empty>
        ) : (
        <Spin spinning={loadingInstruments}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder={field.placeholder}
            style={{ width: "100%" }}
            value={selectedInstrumentIds}
            onChange={(values) => {
              const nextSelections = {};
              (values || []).forEach((id) => {
                nextSelections[id] = questionSelections[id] || [];
              });
              setState((prev) => ({
                ...prev,
                feedbackInstrumentIds: values,
                feedbackQuestionSelections: nextSelections,
              }));
            }}
            options={instruments.map((item) => ({
              value: item.id,
              label: item.title,
            }))}
            notFoundContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("new_dashboard.feedbackCapture.no_instruments")}
              />
            }
          />
        </Spin>
        )}
      </div>
    );
  }

  if (field.valueType === "feedbackQuestions") {
    return (
      <div>
        <h4>{field.title}</h4>
        {field.subtitle && <p>{field.subtitle}</p>}
        {!selectedInstrumentIds.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("new_dashboard.feedbackCapture.pick_instrument_first")}
          />
        ) : (
          <Spin spinning={loadingConfig}>
            {selectedInstrumentIds.map((instrumentId) => {
              const config = instrumentConfigs[instrumentId] || {};
              const questions = (config.questions || []).filter((q) =>
                ANALYZABLE_TYPES.has(String(q?.type || "").toLowerCase())
              );
              const selected = questionSelections[instrumentId] || [];
              return (
                <div key={instrumentId} style={{ marginBottom: 20 }}>
                  <Text strong>
                    {instrumentTitleMap[instrumentId] ||
                      t("new_dashboard.feedbackCapture.survey_fallback", {
                        id: instrumentId,
                      })}
                  </Text>
                  {questions.length ? (
                    <Checkbox.Group
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginTop: 8,
                      }}
                      value={selected}
                      onChange={(values) =>
                        setState((prev) => ({
                          ...prev,
                          feedbackQuestionSelections: {
                            ...(prev.feedbackQuestionSelections || {}),
                            [instrumentId]: values,
                          },
                        }))
                      }
                    >
                      {questions.map((q) => (
                        <Checkbox key={q.id} value={q.id}>
                          {q.text || q.id}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("new_dashboard.feedbackCapture.no_questions")}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              );
            })}
          </Spin>
        )}
      </div>
    );
  }

  return null;
};

export default FeedbackCaptureWizardFields;
